import type { IPropertyGroup } from "../../properties/common/properties.js";
import type { CodeOption } from "./codeView.js";

/**
 * Builds the code-source options a walked property collection offers — every
 * enabled `get-sourcecode` action becomes one {@link CodeOption}. Shared by
 * the code panel's selection follow (`PropertyService` reports these to
 * `ICodeViewService`) and the AI `code_read` tool, so both see the identical
 * menu for an object.
 *
 * The picker section comes EXPLICITLY from the group's category (set by the
 * property provider), never inferred from labels or counts:
 *   "three" (the object itself) → "Object" section, row = owner name
 *   "materials" → one section PER material, plain source row = "Code"
 *   "components" → one shared "Components" section, row = component name
 * Uncategorized groups render ungrouped — surfacing the missing category
 * beats silently guessing a section.
 */
/**
 * Marks a `get-sourcecode` action's produced text as an EDITABLE project
 * file's content — {@link collectCodeOptions} lifts it onto
 * {@link CodeOption.file}. A typed side-field (the Action class has no
 * metadata bag); producers set it, only this module reads it.
 */
export interface FileSourceAction {
    fileSource?: { path: string };
}

export function collectCodeOptions(groups: readonly IPropertyGroup[]): CodeOption[] {
    const options: CodeOption[] = [];
    const usedIds = new Set<string>();
    for (const group of groups) {
        let section: string | undefined;
        let plainLabel = group.name;
        // Owner kind + type name (a CLASS name — "MeshStandardNodeMaterial",
        // "Mesh", a component class — never a user-authored instance name);
        // carried on each option for analytics and generic consumers. The
        // object group stores its class name in `id` (its `name` is the literal
        // "Object"); material/component groups store it in `name`.
        let category: CodeOption["category"];
        let ownerType: string | undefined;
        if (group.categories.includes("components")) { section = "Components"; category = "component"; ownerType = group.name; }
        else if (group.categories.includes("three")) { section = "Object"; category = "object"; ownerType = group.id; }
        else if (group.categories.includes("materials")) { section = group.name; plainLabel = "Code"; category = "material"; ownerType = group.name; }
        // Disabled = the source isn't readable for this object (e.g. a
        // built-in material's compiled shaders) — not a valid option.
        const actions = group.actions.filter(a => a.id === "get-sourcecode" && a.enabled !== false);
        for (const action of actions) {
            // "View Fragment Shader" → label "Fragment Shader", kind "fragment-shader".
            let label = action.label.replace(/^view\s+/i, "").trim() || "Code";
            let kind = label.toLowerCase().replace(/\s+/g, "-");
            // producer-marked editable project file (see FileSourceAction):
            // the picker shows the FILENAME — "Source" says nothing when the
            // code IS a file — under one sticky kind for all file sources
            const fileSource = (action as FileSourceAction).fileSource;
            const fileName = fileSource?.path.split("/").pop();
            if (fileSource && fileName) {
                label = fileName;
                kind = "file-source";
            }
            // Two same-named groups (e.g. two DragControls components) must
            // not collide — ids key the selection AND the rendered rows.
            let id = `${group.name}:${action.label}`;
            for (let i = 2; usedIds.has(id); i++) id = `${group.name}:${action.label} (${i})`;
            usedIds.add(id);
            options.push({
                id,
                kind,
                label: kind === "code" ? plainLabel : label,
                group: section,
                category,
                ownerType,
                // Shader sources (authored or compiled) highlight as GLSL —
                // close enough for WGSL too until hljs ships a wgsl grammar.
                language: /shader/i.test(label) ? "glsl" : undefined,
                file: fileSource,
                produce: async () => {
                    const r = await action.run();
                    return typeof r === "string" ? r : null;
                },
            });
        }
    }
    return options;
}
